import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Props as BenefitsItemProps } from 'app/components/Benefits/Item'
import { Button } from 'app/components/Common/Button'
import { Props as MenuItemProps } from 'app/components/Common/Header/MenuItem'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { theme } from 'app/theme'
import { Media, MediaContextProvider, mediaStyle } from 'app/theme/media'
import { useVocabularyData } from 'app/utils/vocabulary'
import disableScroll from 'disable-scroll'
import { rgba } from 'emotion-rgba'
import { Link } from 'gatsby'
import React, { memo, useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import ReactGA from 'react-ga'

import { LanguageItem, Props as LanguageItemProps } from './LanguageItem'
import { ModalClose } from './ModalClose'
import { ModalIBE } from './ModalIBE'
import { Navigation } from './Navigation'

export interface Props {
  address?: string
  backgroundImage?: { [key: string]: any }
  benefitsDescription?: string
  benefitsChecklist?: BenefitsItemProps[]
  country?: string
  email?: string
  IBEID?: number
  phone?: string
  postalCode?: string
  languageCode: string
  languagePrefix: string | null
  languagesList: LanguageItemProps[]
  logo?: string
  logoSticky?: string
  menuItems: MenuItemProps[]
  pageID?: string
  locality?: string
  region?: string
  siteName?: string
  variant?: Variant
}

export const Header = memo(function Header({
  address,
  backgroundImage,
  benefitsDescription,
  benefitsChecklist,
  country,
  email,
  IBEID,
  phone,
  postalCode,
  languageCode,
  languagePrefix,
  languagesList,
  logo,
  logoSticky,
  menuItems,
  pageID,
  locality,
  region,
  siteName,
  variant = 'default',
}: Props) {
  const languages = languagesList.filter((t) => t.pageID === pageID)

  const activeLanguage = useRef(null)
  const [scroll, setScroll] = useState(false)
  const [navigationStatus, setNavigationStatus] = useState(false)
  const [othersLanguagesStatus, setOthersLanguagesStatus] = useState(false)
  const [modalIBEStatus, setModalIBEStatus] = useState(false)

  const handleClickOutside = (e: any) => {
    // @ts-ignore
    if (activeLanguage.current && !activeLanguage.current.contains(e.target)) {
      setOthersLanguagesStatus(false)
    } else {
      // @ts-ignore
      if (othersLanguagesStatus) {
        setOthersLanguagesStatus(false)
      } else {
        setOthersLanguagesStatus(true)
      }
    }
  }

  useEffect(() => {
    const onScroll = () => {
      setScroll(window.scrollY > window.innerHeight)
    }

    if (window.innerWidth > 767) {
      disableScroll.off()
    } else {
      document.body.style.overflow = 'auto'
    }

    window.addEventListener('scroll', onScroll)
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      window.removeEventListener('scroll', onScroll)
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [othersLanguagesStatus])

  return (
    <Container>
      <style type="text/css">{mediaStyle}</style>
      <MediaContextProvider>
        <Head
          className={scroll || variant === 'compact' ? 'fixed' : ''}
          dial={6}
          row
          variant={variant}
        >
          <Hamburger
            className="hamburger-menu"
            onClick={() => {
              if (!navigationStatus) {
                setNavigationStatus(true)
                if (window.innerWidth > 767) {
                  disableScroll.on()
                } else {
                  document.body.style.overflow = 'hidden'
                }
              } else {
                setNavigationStatus(false)
              }
            }}
          >
            <div />
          </Hamburger>
          <Link to={languagePrefix ? `/${languagePrefix}` : '/'}>
            {/* {logoSticky ? (
              <Media lessThan="ipadHorizontal">
                <Logo
                  className="logo"
                  src={logoSticky}
                  alt={siteName}
                  width="151"
                  height="120"
                />
              </Media>
            ) : null} */}
            {logo ? (
              <Media greaterThanOrEqual="ipadHorizontal">
                <Logo
                  className="logo"
                  /* src={scroll || variant === 'compact' ? logoSticky : logo} */
                  src={scroll || variant === 'compact' ? logo : logo}
                  alt={siteName}
                  width="151"
                  height="120"
                />
              </Media>
            ) : null}
          </Link>
          {phone ? (
            <Phone
              className="header-phone"
              href={`tel:${phone}`}
              onClick={() => {
                if (isMobile) {
                  ReactGA.event({
                    category: 'Website',
                    action: 'Click',
                    label: 'Mobile Phone',
                  })
                }
              }}
            >
              {phone}
            </Phone>
          ) : null}
          <Languages className="header-langs" ref={activeLanguage}>
            <LanguageActive
              className={othersLanguagesStatus ? 'open' : undefined}
              dial={4}
              row
            >
              {languages
                .filter((t) => t.languagePrefix === languagePrefix)
                .map((item, index) => (
                  <LanguageItem key={index} {...item} />
                ))}
              <svg width="9" height="7" viewBox="0 0 9 7">
                <path d="M4.5,0,9,7H0Z" />
              </svg>
            </LanguageActive>
            <OthersLanguages
              className={othersLanguagesStatus ? 'open' : undefined}
            >
              {languagesList
                .filter((t) => t.languagePrefix !== languagePrefix)
                .map((item, index) => (
                  <LanguageItem key={index} {...item} />
                ))}
            </OthersLanguages>
          </Languages>
          <Media greaterThan="tabletMedium">
            <CTA
              arrow
              label={useVocabularyData('book', languageCode)}
              onClick={() => {
                if (!modalIBEStatus) {
                  setModalIBEStatus(true)
                  disableScroll.on()
                } else {
                  setModalIBEStatus(false)
                  disableScroll.off()
                }
              }}
            />
          </Media>
          <Media between={['ipadVertical', 'ipadHorizontal']}>
            <CTA
              arrow
              URL={`https://www.simplebooking.it/ibe/hotelbooking/search?hid=${IBEID}&lang=${
                languagePrefix ? languagePrefix.toLocaleUpperCase() : 'IT'
              }`}
              label={useVocabularyData('book', languageCode)}
              rel="noopener"
              target="_blank"
            />
          </Media>
        </Head>
        <Modal className={navigationStatus ? 'open' : ''}>
          <ModalClose
            onClick={() => {
              if (window.innerWidth > 767) {
                disableScroll.off()
              } else {
                document.body.style.overflow = 'auto'
              }
              setNavigationStatus(false)
            }}
          />
          <Navigation
            address={address}
            backgroundImage={backgroundImage}
            country={country}
            email={email}
            IBEID={IBEID}
            languageCode={languageCode}
            languagePrefix={languagePrefix || 'it'}
            logo={logo}
            locality={locality}
            menuItems={menuItems}
            navigationStatus={navigationStatus}
            phone={phone}
            postalCode={postalCode}
            region={region}
            siteName={siteName}
          />
        </Modal>
        <Media greaterThanOrEqual="ipadVertical">
          <Modal className={modalIBEStatus ? 'open' : ''}>
            <ModalClose
              onClick={() => {
                disableScroll.off()
                setModalIBEStatus(false)
              }}
              variant="left"
            />
            <ModalIBE
              IBEID={IBEID}
              languagePrefix={languagePrefix || 'it'}
              benefitsDescription={benefitsDescription}
              benefitsChecklist={benefitsChecklist}
              modalStatus={modalIBEStatus}
            />
          </Modal>
        </Media>
      </MediaContextProvider>
    </Container>
  )
})

const Container = styled.section`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
`

const HeaderFixedStyle = css`
  background: ${theme.colors.variants.neutralLight4};
  box-shadow: ${rgba(theme.colors.variants.neutralDark1, 0.1)} 0 3px 60px;
  margin-top: 0;
  position: fixed;
  top: 0;
  left: 0;
  .logo {
    height: 50px;
    margin-top: 0.5625rem;
  }
  .hamburger-menu {
    &:before,
    &:after {
      background: ${theme.colors.variants.neutralDark4};
    }
    > div {
      background: ${theme.colors.variants.neutralDark4};
    }
  }
  .header-phone {
    color: ${theme.colors.variants.neutralDark4};
  }
  .header-langs {
    margin-right: 1rem;
    > div {
      &:first-of-type {
        &:before {
          opacity: 1;
        }
      }
      &:last-of-type {
        width: calc(100% + 1rem);
        background: ${theme.colors.variants.neutralLight4};
        top: 2.5rem;
      }
      a {
        color: ${theme.colors.variants.neutralDark4};
      }
    }
  }
`

const Head = styled(FlexBox)<HeadProps>`
  width: 100%;
  margin-top: 2.25rem;
  padding: 0 2.25rem;
  position: relative;
  &.fixed {
    ${HeaderFixedStyle}
  }

  ${({ variant }) => {
    switch (variant) {
      case 'default':
        return css`
          &.fixed {
            animation: fadeInTop 0.3s;
          }
        `
    }
  }}

  @media (max-width: 1023px) {
    ${HeaderFixedStyle}
    animation: none !important;
    padding: 0 1.5rem;
    .logo {
      height: 2.875rem !important;
      margin-top: 0.75rem !important;
    }
  }

  @media (max-width: 767px) {
    .header-langs {
      margin-right: 0 !important;
    }
  }

  @keyframes fadeInTop {
    0% {
      top: -3.75rem;
    }
    100% {
      top: 0;
    }
  }
`

const Logo = styled.img`
  width: auto;
  margin: auto;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
`

const Hamburger = styled.div`
  cursor: pointer;
  width: 36px;
  margin-right: auto;
  position: relative;
  &:before,
  &:after {
    content: '';
    display: block;
    width: 100%;
    height: 2px;
    background: ${({ theme }) => theme.colors.variants.neutralLight4};
    border-radius: 2px;
  }
  &:hover {
    > div {
      width: 100%;
    }
  }
  > div {
    width: 17px;
    height: 2px;
    background: ${({ theme }) => theme.colors.variants.neutralLight4};
    border-radius: 2px;
    margin: 12px auto;
    transition: 0.3s ease;
  }

  @media (max-width: 767px) {
    width: 30px;
    > div {
      width: 15px;
      margin: 8px auto;
    }
  }
`

const Phone = styled.a`
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.8125rem;
  margin-right: 1.875rem;
  &:hover {
    text-decoration: underline;
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const Languages = styled.div`
  margin-right: 1.875rem;
  position: relative;
  z-index: 2;
`

const LanguageActive = styled(FlexBox)`
  cursor: pointer;
  &:before {
    content: '';
    display: block;
    width: 1px;
    height: 70px;
    margin-right: 1rem;
    opacity: 0;
    background: ${({ theme }) => theme.colors.variants.neutralLight2};
  }
  &.open {
    svg {
      transform: scaleY(1) translateY(-1px);
    }
  }
  svg {
    fill: ${({ theme }) => theme.colors.variants.primaryDark1};
    margin-left: 0.75rem;
    transform: scaleY(-1);
    transition: 0.3s ease-in-out;
  }

  @media (max-width: 1023px) {
    &:before {
      height: 60px;
    }
  }

  @media (max-width: 767px) {
    &:before {
      background: ${({ theme }) => theme.colors.variants.neutralLight4};
    }
  }
`

const OthersLanguages = styled.div`
  width: 2.125rem;
  opacity: 0;
  padding: 0 1rem;
  position: absolute;
  top: 1.875rem;
  left: 0;
  transform: translateY(2.25rem);
  transition: 0.3s ease-in-out;
  visibility: hidden;
  z-index: -1;
  &.open {
    opacity: 1;
    transform: translateY(1.5rem);
    visibility: visible;
    z-index: 1;
  }
  a {
    display: block;
    margin-bottom: 0.5625rem;
  }
`

const CTA = styled(Button)`
  margin-top: 0;

  @media (max-width: 1023px) {
    margin-top: 0;
    padding-right: 1.5rem;
    padding-left: 1.5rem;
    svg {
      display: none;
    }
  }

  @media (max-width: 767px) {
    display: none;
  }
`

const Modal = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  opacity: 0;
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  z-index: -1;
  transition: 0.15s ease-out;
  &.open {
    opacity: 1;
    visibility: visible;
    z-index: 99;
  }
`

interface HeadProps {
  variant: Variant
}

export type Variant = 'default' | 'compact'
